<script lang="ts">
  import { Link } from 'svelte-routing';
  import { api } from '../../api/client';
  import type { Destination, Drives, MemberLocation, Place, TimelineDay, TimelineStay, TimelineTrip } from '../../api/types';
  import { activityIcon, activityLabel, length } from '../../utils/activities';
  import { usesMiles } from '../../stores/units.svelte';
  import { distanceM } from '../../utils/geo';
  import { kindIcon, trip as tripLine, week as weekLines } from '../../utils/driving';
  import { iconClass } from '../../utils/icons';
  import type { MemberState } from '../../utils/memberState';
  import { age, speed, wakeCause } from '../../utils/memberState';
  import { clock } from '../../utils/time';
  import { summaryLines } from '../../utils/health';
  import Avatar from '../Avatar.svelte';
  import DestinationPicker, { type Target } from '../eta/DestinationPicker.svelte';
  import { minutes, secondsLeft } from '../../utils/eta';
  import type { DayCard } from './dayCards';
  import { decimal, integer, locale, t, weekday as weekdayName } from '../../i18n/index.ts';

  // MemberPanel is a selected member (spec 010 FR-012, 010 FR-013, 010 FR-014, 010 FR-016):
  // who they are and how current, what they are doing, actions, their day
  // so far, and the week's drives.
  interface Props {
    groupId: string;
    viewerId: string;
    member: MemberLocation;
    st: MemberState;
    viewer?: { lat: number; lon: number };
    // places are the viewer's groups' places, offered as destinations.
    places: Place[];
    live?: { state: string; reason?: string };
    followError: string;
    now: number;
    picked: string | null;
    onClose: () => void;
    onFollowAgain: () => void;
    onDay: (cards: DayCard[]) => void;
    onPick: (card: DayCard) => void;
  }
  let { groupId, viewerId, member, st, viewer, places, live, followError, now, picked, onClose, onFollowAgain, onDay, onPick }: Props = $props();

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // miles is whether distance and speed show in miles (010 FR-034).
  let miles = $derived(usesMiles());
  let day = $state<TimelineDay | null>(null);
  let dayError = $state('');
  let drives = $state<Drives | null>(null);

  // localDate is a date as YYYY-MM-DD in the viewer's zone.
  function localDate(d: Date): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  }

  // weekStart is the first day of this week in the viewer's locale.
  function weekStart(): string {
    const locale = new Intl.Locale(navigator.language) as Intl.Locale & { getWeekInfo?: () => { firstDay: number }; weekInfo?: { firstDay: number } };
    const first = (locale.getWeekInfo?.() ?? locale.weekInfo)?.firstDay ?? 1;
    const d = new Date();
    const dow = d.getDay() === 0 ? 7 : d.getDay();
    d.setDate(d.getDate() - ((dow - first + 7) % 7));
    return localDate(d);
  }

  function stayName(s: TimelineStay): string {
    return s.name || s.address || t('mapUnnamedPlace');
  }

  function stayIcon(s: TimelineStay): string {
    if (s.icon) return s.icon;
    return ({ home: 'home', work_education: 'work', shopping: 'shopping', health: 'health', outdoors: 'sport' } as Record<string, string>)[s.category ?? ''] ?? 'pin';
  }

  const modeIcon: Record<string, string> = { driving: 'car', walking: 'walking', running: 'running', cycling: 'bicycle', bus: 'bus', train: 'train', flight: 'plane' };

  function cardsOf(d: TimelineDay): DayCard[] {
    const stays = [...d.stays].sort((a, b) => a.start.localeCompare(b.start));
    const trips: DayCard[] = d.trips.filter((tr) => !tr.missing_visit).map((tr) => {
      const before = stays.filter((s) => s.start <= tr.start).pop();
      const after = tr.ongoing ? undefined : stays.find((s) => s.start >= tr.start);
      return { kind: 'trip', start: tr.start, trip: tr, from: before ? stayName(before) : undefined, to: after ? stayName(after) : undefined };
    });
    return [...stays.map((s) => ({ kind: 'stay', start: s.start, stay: s }) as DayCard), ...trips].sort((a, b) => b.start.localeCompare(a.start));
  }

  const cards = $derived(day ? cardsOf(day) : []);
  $effect(() => onDay(cards));

  // manual is a member the viewer sees only when they choose to share.
  const manual = $derived(member.tracking_mode === 'manual');

  // The day reloads every minute; the week once.
  $effect(() => {
    const userId = member.user_id;
    day = null;
    dayError = '';
    drives = null;
    // A member seen only when they choose has no day or drives to read
    // (spec 001 FR-052); asking would only be refused.
    if (manual) return;
    const load = () => api.get<TimelineDay>(`/groups/${groupId}/members/${userId}/timeline/days/${localDate(new Date())}?tz=${encodeURIComponent(tz)}`)
      .then((d) => { day = d; dayError = ''; })
      .catch((e: any) => { if (!day) dayError = t('mapDayLoadFailed', { error: e.message }); });
    load();
    api.get<Drives>(`/groups/${groupId}/members/${userId}/drives?from=${weekStart()}&tz=${encodeURIComponent(tz)}`).then((d) => (drives = d)).catch(() => {});
    const timer = setInterval(load, 60_000);
    return () => clearInterval(timer);
  });

  function short(meters: number): string {
    if (miles) {
      const mi = meters / 1609.344;
      if (mi < 0.1) return t('mapFeet', { n: integer(meters * 3.28084) });
      return t('mapMiles', { n: mi < 10 ? decimal(mi, 1) : integer(mi) });
    }
    if (meters < 1000) return t('mapMeters', { n: integer(meters) });
    return t('mapKilometers', { n: meters < 10_000 ? decimal(meters / 1000, 1) : integer(meters / 1000) });
  }

  function hours(seconds: number): string {
    const m = Math.round(seconds / 60);
    if (m < 60) return t('mapDurationMinutes', { n: m });
    return m % 60 ? t('mapDurationHoursMinutes', { hours: integer(Math.floor(m / 60)), minutes: integer(m % 60) }) : t('mapDurationHours', { n: m / 60 });
  }

  function span(start: string, end: string, ongoing?: boolean): string {
    return ongoing ? t('mapTimeSpanNow', { start: clock(start) }) : t('mapTimeSpan', { start: clock(start), end: clock(end) });
  }

  // sketch is an SVG path of a trip's shape, with no map under it.
  function sketch(trip: TimelineTrip): { d: string; start: [number, number]; end: [number, number] } | null {
    const pts = trip.route;
    if (pts.length < 2) return null;
    const lats = pts.map((p) => p.lat);
    const lons = pts.map((p) => p.lon);
    const kx = Math.cos(((Math.min(...lats) + Math.max(...lats)) / 2) * Math.PI / 180);
    const spanDeg = Math.max((Math.max(...lons) - Math.min(...lons)) * kx, Math.max(...lats) - Math.min(...lats), 1e-6);
    const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const midLon = (Math.min(...lons) + Math.max(...lons)) / 2;
    const at = (lat: number, lon: number): [number, number] => [32 + ((lon - midLon) * kx / spanDeg) * 44, 32 - ((lat - midLat) / spanDeg) * 44];
    const xy = pts.map((p) => at(p.lat, p.lon));
    return { d: xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(''), start: xy[0], end: xy[xy.length - 1] };
  }

  const weekday = (iso: string) => weekdayName(new Date(iso));
  const fromWords = (date: string) => new Intl.DateTimeFormat(locale(), { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(`${date}T12:00:00`));

  // tripRoute names where a trip went from and to, with words for an
  // unknown end and for a trip still going on.
  function tripRoute(c: Extract<DayCard, { kind: 'trip' }>): string {
    const ongoing = !c.to && c.trip.ongoing;
    if (c.from) {
      if (c.to) return t('mapTripRoute', { from: c.from, to: c.to });
      return ongoing ? t('mapTripRouteToNow', { from: c.from }) : t('mapTripRouteToUnknown', { from: c.from });
    }
    if (c.to) return t('mapTripRouteFromUnknown', { to: c.to });
    return ongoing ? t('mapTripRouteFromUnknownToNow') : t('mapTripRouteUnknown');
  }
  const stateColor = $derived(
    st.kind === 'PROBLEM' ? 'var(--theme-error)'
      : st.kind === 'PAUSED' || st.kind === 'MANUAL' ? 'var(--theme-warning)'
        : st.kind === 'AT_PLACE' ? 'var(--theme-success)' : st.kind === 'MOVING' ? 'var(--theme-accent)' : 'var(--theme-subtext0)',
  );
  // Destinations (spec 003): the viewer's own for the member and the
  // member's heading, each with its ETA in words.
  const self = $derived(member.user_id === viewerId);
  const first = $derived(member.display_name.split(' ')[0]);
  const destinations = $derived([member.destination, member.heading].filter((d): d is Destination => !!d));
  let picking = $state<'watch' | 'heading' | null>(null);
  let etaError = $state('');

  function destinationTitle(d: Destination): string {
    if (d.kind === 'heading') return self ? t('mapHeadingYou', { place: d.name }) : t('mapHeadingMember', { place: d.name });
    return d.name === 'your location' ? t('mapUntilReachesYou', { member: first }) : t('mapUntilReachesPlace', { member: first, place: d.name });
  }

  async function act(f: () => Promise<unknown>) {
    etaError = '';
    try {
      await f();
    } catch (e: any) {
      etaError = e.message;
    }
  }

  const pickTarget = (target: Target) => picking === 'heading'
    ? api.put('/me/heading', target).then(() => {})
    : api.post(`/groups/${groupId}/members/${member.user_id}/destination`, target).then(() => {});

  const distance = $derived(member.location && viewer && member.user_id !== viewerId
    ? short(distanceM(viewer.lat, viewer.lon, member.location.lat, member.location.lon)) : null);
</script>

{#if picking}
  <DestinationPicker
    title={picking === 'heading' ? t('mapHeadingTo') : t('mapHowLongUntilMember', { member: first })}
    confirm={picking === 'heading' ? t('mapTellMyGroups') : t('mapShowEta')}
    start={picking === 'heading' ? (member.location ? { lat: member.location.lat, lon: member.location.lon } : viewer) : viewer}
    mine={picking === 'watch'}
    {places}
    onPick={pickTarget}
    onclose={() => (picking = null)}
  />
{/if}

<div class="flex flex-col min-h-0 flex-1">
  <div class="p-4 flex items-center gap-3 border-b" style="border-color: var(--theme-surface0);">
    <Avatar name={member.display_name} userId={member.user_id} color={member.color} photo={member.photo} size={48} dot={st.current} />
    <div class="flex-1 min-w-0">
      <h2 class="text-lg font-semibold truncate">{member.display_name}</h2>
      <p class="text-xs" style="color: {live?.state === 'streaming' ? 'var(--theme-success)' : live?.state === 'unreachable' ? 'var(--theme-error)' : 'var(--theme-subtext0)'};">
        {#if live}
          {live.reason ? t('mapLiveStateCause', { state: live.state, cause: wakeCause(live.reason) }) : t('mapLiveState', { state: live.state })}
        {:else if member.location}
          {t('mapUpdated', { age: age(member.location.recorded_at, now) })}
        {:else}
          {t('mapNoLocation')}
        {/if}
      </p>
    </div>
    {#if member.location?.battery_pct != null}
      <span class="chip" class:low={st.lowBattery}><i aria-hidden="true" class="las {member.location.charging ? 'la-bolt' : 'la-battery-half'}"></i><span aria-hidden="true">{t('mapBatteryPercent', { percent: integer(member.location.battery_pct) })}</span><span class="sr-only">{member.location.charging ? t('mapBatteryCharging', { percent: integer(member.location.battery_pct) }) : t('mapBatteryLevel', { percent: integer(member.location.battery_pct) })}</span></span>
    {/if}
    <button class="icon-btn" onclick={onClose} aria-label={t('appClose')}><i aria-hidden="true" class="las la-times"></i></button>
  </div>

  <div class="flex-1 overflow-y-auto">
    <div class="px-4 pt-3 space-y-1">
      <p class="flex items-center gap-2 font-semibold" style="color: {st.kind === 'PROBLEM' || st.kind === 'PAUSED' || st.kind === 'MANUAL' ? stateColor : 'var(--theme-text)'};">
        <span class="state-icon" style="color: {stateColor}; background: color-mix(in srgb, {stateColor} 15%, transparent);"><i aria-hidden="true" class={iconClass(st.icon)}></i></span>
        {st.headline}
      </p>
      {#if st.detail}<p class="text-sm text-text-muted pl-10">{st.detail}</p>{/if}
      {#each st.notes as n (n.text)}
        <p class="text-xs pl-10" style="color: {n.severe ? 'var(--theme-error)' : 'var(--theme-subtext0)'};">
          <i aria-hidden="true" class="las {n.severe ? 'la-exclamation-circle' : 'la-car-crash'} mr-1"></i>{n.text}
        </p>
      {/each}
      {#if followError}<p class="text-xs pl-10" style="color: var(--theme-warning);">{followError}</p>{/if}
      {#if member.health_summary}
        <!-- A group with Resting heart rate and sleep sees these two and nothing more (spec 007 FR-027). -->
        {#each summaryLines(member.health_summary) as line (line.text)}
          <p class="text-sm pl-10"><i aria-hidden="true" class="las {line.icon} mr-1"></i>{line.text}</p>
        {/each}
      {/if}
    </div>

    {#each destinations as d (d.id)}
      <div class="card-row mx-4 mt-3">
        <p class="flex items-center gap-2 text-sm font-semibold">
          <i aria-hidden="true" class="las la-flag-checkered text-xl" style="color: var(--theme-accent);"></i>{destinationTitle(d)}
        </p>
        {#if d.eta}
          {@const left = secondsLeft(d.eta, now)}
          {@const arrive = clock(new Date(d.eta.moving ? d.eta.arrive_at : now + left * 1000).toISOString())}
          <p class="text-lg font-semibold mt-1">{t('mapEtaAbout', { duration: minutes(left), distance: short(d.eta.distance_m) })}</p>
          <p class="text-sm">{d.eta.moving ? t('mapArriving', { time: arrive }) : self ? t('mapArrivingIfYouLeave', { time: arrive }) : t('mapArrivingIfTheyLeave', { time: arrive })}</p>
          <p class="text-xs text-text-muted mt-1">
            {d.eta.kind === 'road' ? t('mapEtaByRoad', { age: age(d.eta.as_of, now) }) : t('mapEtaEstimate', { age: age(d.eta.as_of, now) })}
          </p>
        {:else}
          <p class="text-sm text-text-muted mt-1">{t('mapEtaWorking')}</p>
        {/if}
        <div class="flex flex-wrap gap-2 mt-2">
          {#if d.kind === 'heading' && !self}
            <label class="flex items-center gap-2 text-sm cursor-pointer flex-1">
              <input type="checkbox" class="toggle" checked={d.watching}
                     onchange={(e) => act(() => (e.target as HTMLInputElement).checked ? api.post(`/destinations/${d.id}/watch`) : api.delete(`/destinations/${d.id}/watch`))} />
              {t('mapTellMeArrives', { member: first })}
            </label>
          {:else if d.kind === 'heading'}
            <button class="pill" onclick={() => act(() => api.delete('/me/heading'))}><i aria-hidden="true" class="las la-times"></i>{t('mapStop')}</button>
          {:else}
            <span class="text-xs text-text-muted flex-1">{t('mapToldWhenArrives', { member: first })}</span>
            <button class="pill" onclick={() => act(() => api.delete(`/groups/${groupId}/members/${member.user_id}/destination`))}><i aria-hidden="true" class="las la-times"></i>{t('appRemove')}</button>
          {/if}
        </div>
      </div>
    {/each}
    {#if etaError}<p class="text-xs px-4 mt-1" style="color: var(--theme-error);">{etaError}</p>{/if}

    <div class="px-4 py-3 flex flex-wrap gap-2">
      {#if (!live || live.state === 'unreachable') && member.tracking_mode !== 'manual' && (followError || live)}
        <button class="pill" onclick={onFollowAgain}><i aria-hidden="true" class="las la-location-arrow"></i>{t('mapFollowAgain')}</button>
      {/if}
      {#if member.location}
        <a class="pill" title={t('mapDirections')} href={`https://www.openstreetmap.org/directions?to=${member.location.lat},${member.location.lon}`} target="_blank" rel="noopener noreferrer">
          <i aria-hidden="true" class="las la-directions"></i>{#if distance}<span aria-hidden="true">{distance}</span><span class="sr-only">{t('mapDirectionsAway', { distance })}</span>{:else}{t('mapDirections')}{/if}
        </a>
      {/if}
      {#if member.phone}
        <a class="pill" href={`tel:${member.phone.replace(/[^\d+]/g, '')}`}><i aria-hidden="true" class="las la-phone"></i>{t('mapCall')}</a>
      {/if}
      {#if member.email}
        <a class="pill" href={`mailto:${member.email}`}><i aria-hidden="true" class="las la-envelope"></i>{t('mapEmail')}</a>
      {/if}
      {#if self}
        <button class="pill" onclick={() => (picking = 'heading')}><i aria-hidden="true" class="las la-flag-checkered"></i>{member.heading ? t('mapChangeHeading') : t('mapHeadingTo')}</button>
      {:else if member.location && member.tracking_mode !== 'manual' && member.status !== 'not_shared' && member.status !== 'paused'}
        <button class="pill" onclick={() => (picking = 'watch')}><i aria-hidden="true" class="las la-hourglass-half"></i>{t('mapHowLongUntil')}</button>
      {/if}
      <Link to={`/timeline/${groupId}/${member.user_id}`} class="pill"><i aria-hidden="true" class="las la-history"></i>{t('mapHistory')}</Link>
      {#if member.activity}
        <Link to={`/activity/${groupId}/${member.activity.id}`} class="pill"><i aria-hidden="true" class="las {activityIcon(member.activity.type)}"></i>{t('mapActivityNow', { activity: activityLabel(member.activity.type) })}</Link>
      {/if}
      <Link to={`/activities/${groupId}/${member.user_id}`} class="pill"><i aria-hidden="true" class="las la-running"></i>{t('mapActivities')}</Link>
    </div>

    <div class="px-4 pt-3 flex items-center justify-between">
      <h3 class="font-semibold">{t('mapToday')}</h3>
      <Link to={`/timeline/${groupId}/${member.user_id}`} class="text-sm">{t('mapTimeline')}</Link>
    </div>
    <div class="px-4 py-2 space-y-2">
      {#if manual}
        <p class="text-sm text-text-muted">{t('mapManualNoDay', { member: member.display_name })}</p>
      {:else if dayError}
        <p class="text-sm" style="color: var(--theme-error);">{dayError}</p>
      {:else if !day}
        <p class="text-sm text-text-muted">{t('mapLoadingToday')}</p>
      {:else if cards.length === 0}
        <p class="text-sm text-text-muted">{t('mapNothingToday')}</p>
      {/if}
      {#each cards as c (c.kind + c.start)}
        <button class="card-row w-full text-left flex items-center gap-3" class:picked={picked === c.start} onclick={() => onPick(c)}>
          {#if c.kind === 'stay'}
            <span class="state-icon big" style="color: var(--theme-success); background: color-mix(in srgb, var(--theme-success) 15%, transparent);"><i aria-hidden="true" class={iconClass(stayIcon(c.stay))}></i></span>
            <span class="flex-1 min-w-0">
              <span class="block text-sm font-semibold truncate">{t('mapStayAt', { place: stayName(c.stay) })}</span>
              <span class="block text-xs text-text-muted">{span(c.stay.start, c.stay.end, c.stay.ongoing)}</span>
              {#if c.stay.ongoing}<span class="chip mt-1"><i aria-hidden="true" class="las la-clock"></i>{t('mapHappeningNow')}</span>{/if}
            </span>
          {:else}
            {@const sk = sketch(c.trip)}
            <svg class="sketch" viewBox="0 0 64 64" aria-hidden="true">
              {#if sk}
                <path d={sk.d} fill="none" stroke="var(--theme-accent)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
                <circle cx={sk.start[0]} cy={sk.start[1]} r="3.5" fill="var(--theme-accent)" />
                <circle cx={sk.end[0]} cy={sk.end[1]} r="4.5" fill="var(--theme-success)" />
              {/if}
            </svg>
            <span class="flex-1 min-w-0">
              <span class="block text-sm font-semibold line-clamp-2">{tripRoute(c)}</span>
              <span class="block text-xs text-text-muted">{t('mapTripSpanDistance', { span: span(c.trip.start, c.trip.end, c.trip.ongoing), distance: length(c.trip.distance_m) })}</span>
              <span class="flex flex-wrap gap-1 mt-1">
                <span class="chip"><i aria-hidden="true" class={iconClass(modeIcon[c.trip.mode] ?? 'route')}></i>{t('mapTripMode', { mode: c.trip.mode })}</span>
                {#if c.trip.top_speed_mps != null}<span class="chip"><i aria-hidden="true" class="las la-tachometer-alt"></i>{t('mapTopSpeedChip', { speed: speed(c.trip.top_speed_mps, miles) })}</span>{/if}
              </span>
              {#if c.trip.driving}
                {@const line = tripLine(c.trip.driving)}
                {#if line.counts}<span class="block text-xs mt-1" style="color: var(--theme-accent);">{line.counts}</span>{/if}
                {#each line.notes as n (n)}<span class="block text-xs text-text-muted">{n}</span>{/each}
              {/if}
            </span>
          {/if}
        </button>
      {/each}
    </div>

    {#if drives && drives.drives > 0}
      <Link to={`/timeline/${groupId}/${member.user_id}`} class="card-row mx-4 my-2 block">
        <div class="flex items-center gap-3">
          <span class="state-icon big" style="color: var(--theme-accent); background: color-mix(in srgb, var(--theme-accent) 15%, transparent);"><i aria-hidden="true" class="las la-car-side"></i></span>
          <span class="flex-1">
            <span class="block text-sm font-semibold">{t('mapWeekDrives')}</span>
            <span class="block text-xs text-text-muted">{t('mapSince', { date: fromWords(drives.from) })}</span>
          </span>
          <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
        </div>
        <div class="grid grid-cols-3 gap-2 mt-3">
          <span class="stat"><b>{integer(drives.drives)}</b>{t('mapDrivesLabel', { n: drives.drives })}</span>
          <span class="stat"><b>{short(drives.distance_m)}</b>{t('mapDriven')}</span>
          <span class="stat"><b>{hours(drives.seconds)}</b>{t('mapBehindWheel')}</span>
        </div>
        {#if drives.top_speed_mps != null}
          <p class="text-sm mt-2"><i aria-hidden="true" class="las la-tachometer-alt mr-1" style="color: var(--theme-accent);"></i>{drives.top_speed_at ? t('mapTopSpeedOn', { speed: speed(drives.top_speed_mps, miles), weekday: weekday(drives.top_speed_at) }) : t('mapTopSpeed', { speed: speed(drives.top_speed_mps, miles) })}</p>
        {/if}
        {#if drives.behavior}
          {@const lines = weekLines(drives.behavior)}
          {#if lines.length}
            <ul class="mt-3 pt-2 border-t space-y-1.5" style="border-color: var(--theme-surface1);">
              {#each lines as l (l.kind)}
                <li class="flex items-center gap-2 text-sm">
                  <i aria-hidden="true" class="las {kindIcon(l.kind)}" style="color: {l.count == null ? 'var(--theme-subtext0)' : 'var(--theme-accent)'};"></i>
                  <span class="flex-1 min-w-0">
                    <span class="block">{l.label}</span>
                    {#if l.note}<span class="block text-xs text-text-muted">{l.note}</span>{/if}
                  </span>
                  {#if l.count != null}<b>{l.count}</b>{/if}
                </li>
              {/each}
            </ul>
          {/if}
        {/if}
        {#if drives.partial}<p class="text-xs text-text-muted mt-1">{t('mapDrivesPartial')}</p>{/if}
      </Link>
    {/if}
  </div>
</div>

<style>
  .icon-btn {
    width: 2rem;
    height: 2rem;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }
  .icon-btn:hover {
    background: var(--theme-surface0);
  }
  .state-icon {
    width: 2rem;
    height: 2rem;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
  }
  .state-icon.big {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 1.3rem;
  }
  :global(.pill) {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.8rem;
    border-radius: 9999px;
    font-size: 0.85rem;
    font-weight: 600;
    border: 1px solid var(--theme-surface1);
    color: var(--theme-text);
  }
  :global(.pill i) {
    color: var(--theme-accent);
    font-size: 1.05rem;
  }
  :global(.pill:hover) {
    background: var(--theme-surface0);
  }
  :global(.card-row) {
    padding: 0.75rem;
    border-radius: 1rem;
    border: 1px solid var(--theme-surface0);
    background: var(--theme-base);
  }
  :global(.card-row.picked) {
    border: 2px solid var(--theme-accent);
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.1rem 0.45rem;
    border-radius: 0.6rem;
    font-size: 0.7rem;
    white-space: nowrap;
    background: var(--theme-surface0);
    color: var(--theme-subtext0);
  }
  .chip.low {
    color: var(--theme-error);
  }
  .sketch {
    width: 4rem;
    height: 4rem;
    border-radius: 0.75rem;
    background: var(--theme-surface0);
    flex-shrink: 0;
  }
  .stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.5rem 0.25rem;
    border-radius: 0.75rem;
    background: var(--theme-surface0);
    font-size: 0.7rem;
    color: var(--theme-subtext0);
    text-align: center;
  }
  .stat b {
    font-size: 1rem;
    color: var(--theme-text);
  }
</style>
